import { Component } from '@theme/component';

/**
 * Bundle Items Component - Tab switching (Premium/Regular),
 * CSS scroll-snap carousel with prev/next arrows and pagination dots.
 *
 * @extends Component
 */
class BundleItemsComponent extends Component {
  /** @type {string} Current active tab */
  #activeTab = 'regular';

  connectedCallback() {
    super.connectedCallback();
    this.#setupScrollListener();
  }

  /**
   * Handle tab button click. Shows/hides cards based on bundle type.
   * @param {Object} data - Parsed query params ({ tab: string })
   * @param {Event} event
   */
  handleTabClick(data, event) {
    const tab = data?.tab || event.target.closest('[data-tab]')?.dataset.tab;
    if (!tab || tab === this.#activeTab) return;

    this.#activeTab = tab;

    // Update tab active states
    const tabButtons = this.refs.tabButtons;
    if (tabButtons) {
      tabButtons.forEach((t) => t.classList.toggle('bundle-items__tab--active', t.dataset.tab === tab));
    }

    // Show/hide cards based on bundle type
    const cards = this.refs.bundleCards;
    if (cards) {
      cards.forEach((card) => {
        card.hidden = card.dataset.bundleType !== tab;
      });
    }

    // Reset scroll position
    const viewport = this.refs.viewport;
    if (viewport) {
      viewport.scrollTo({ left: 0 });
    }

    // Update dots for visible cards
    this.#rebuildDots();
  }

  /**
   * Handle previous arrow click.
   * @param {Event} event
   */
  handlePrev(event) {
    const viewport = this.refs.viewport;
    if (!viewport) return;

    const card = viewport.querySelector('.bundle-items__card:not([hidden])');
    if (!card) return;

    const cardWidth = card.offsetWidth + 16;
    viewport.scrollBy({ left: -cardWidth, behavior: 'smooth' });
  }

  /**
   * Handle next arrow click.
   * @param {Event} event
   */
  handleNext(event) {
    const viewport = this.refs.viewport;
    if (!viewport) return;

    const card = viewport.querySelector('.bundle-items__card:not([hidden])');
    if (!card) return;

    const cardWidth = card.offsetWidth + 16;
    viewport.scrollBy({ left: cardWidth, behavior: 'smooth' });
  }

  /**
   * Handle dot click to scroll to a specific page.
   * @param {Object} data - Parsed query params ({ index: number })
   * @param {Event} event
   */
  handleDotClick(data, event) {
    const viewport = this.refs.viewport;
    if (!viewport) return;

    const index = typeof data?.index === 'number' ? data.index : 0;
    const card = viewport.querySelector('.bundle-items__card:not([hidden])');
    if (!card) return;

    const cardWidth = card.offsetWidth + 16;
    const visibleCards = Math.max(1, Math.floor(viewport.offsetWidth / cardWidth));
    const scrollTarget = index * visibleCards * cardWidth;

    viewport.scrollTo({ left: scrollTarget, behavior: 'smooth' });
  }

  /**
   * Rebuild dots based on visible card count.
   */
  #rebuildDots() {
    const dotsContainer = this.refs.dots;
    const viewport = this.refs.viewport;
    if (!dotsContainer || !viewport) return;

    const visibleCards = viewport.querySelectorAll('.bundle-items__card:not([hidden])');
    if (visibleCards.length <= 1) {
      dotsContainer.innerHTML = '';
      return;
    }

    const cardWidth = visibleCards[0]?.offsetWidth || 250;
    const cardsPerView = Math.max(1, Math.floor(viewport.offsetWidth / cardWidth));
    const dotCount = Math.max(1, Math.ceil(visibleCards.length / cardsPerView));

    dotsContainer.innerHTML = '';
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('button');
      dot.className = `bundle-items__dot${i === 0 ? ' bundle-items__dot--active' : ''}`;
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.setAttribute('on:click', `bundle-items-component/handleDotClick?index=${i}`);
      dotsContainer.appendChild(dot);
    }
  }

  /**
   * Set up scroll listener for dot updates.
   */
  #setupScrollListener() {
    const viewport = this.refs.viewport;
    const dotsContainer = this.refs.dots;
    if (!viewport || !dotsContainer) return;

    let scrollTimeout;
    viewport.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.#updateActiveDot();
      }, 100);
    }, { passive: true });
  }

  /**
   * Update active dot based on scroll position.
   */
  #updateActiveDot() {
    const viewport = this.refs.viewport;
    const dotsContainer = this.refs.dots;
    if (!viewport || !dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('.bundle-items__dot');
    if (!dots.length) return;

    const visibleCards = viewport.querySelectorAll('.bundle-items__card:not([hidden])');
    const cardWidth = visibleCards[0]?.offsetWidth || 250;
    const cardsPerView = Math.max(1, Math.floor(viewport.offsetWidth / cardWidth));
    const currentPage = Math.round(viewport.scrollLeft / ((cardWidth + 16) * cardsPerView));

    dots.forEach((dot, i) => {
      dot.classList.toggle('bundle-items__dot--active', i === currentPage);
    });
  }
}

if (!customElements.get('bundle-items-component')) {
  customElements.define('bundle-items-component', BundleItemsComponent);
}
